import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-jalaali';
import PersianDatePicker from '../components/PersianDatePicker';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { canAccessCallCenterReport } from '../utils/callCenterReportAccess';

const toPersianDigits = (s: string): string =>
  s.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

/** زمان ایران (UTC+3:30) برای هم‌خوانی با CDR Issabel */
const iranMoment = () => moment().utcOffset(210);

const jalaliToGregorianYmd = (jalali: string): string | null => {
  const parts = jalali.split('/');
  if (parts.length !== 3) return null;
  const jy = parseInt(parts[0], 10);
  const jm = parseInt(parts[1], 10);
  const jd = parseInt(parts[2], 10);
  if (Number.isNaN(jy) || Number.isNaN(jm) || Number.isNaN(jd)) return null;
  const m = moment().jYear(jy).jMonth(jm - 1).jDate(jd);
  if (!m.isValid()) return null;
  return m.format('YYYY-MM-DD');
};

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins <= 0) return `${secs.toLocaleString('fa-IR')} ثانیه`;
  return `${mins.toLocaleString('fa-IR')} دقیقه و ${secs.toLocaleString('fa-IR')} ثانیه`;
};

const DurationMetricValue: React.FC<{ seconds: number; valueClass: string }> = ({
  seconds,
  valueClass,
}) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const unitClass = 'text-sm font-medium text-gray-500';

  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return (
      <div className={`flex flex-wrap items-baseline gap-x-2 gap-y-1 ${valueClass}`}>
        <span className="text-2xl font-bold tabular-nums sm:text-3xl">{h.toLocaleString('fa-IR')}</span>
        <span className={unitClass}>ساعت</span>
        {m > 0 && (
          <>
            <span className="text-2xl font-bold tabular-nums sm:text-3xl">{m.toLocaleString('fa-IR')}</span>
            <span className={unitClass}>دقیقه</span>
          </>
        )}
      </div>
    );
  }

  if (mins > 0) {
    return (
      <div className={`flex flex-wrap items-baseline gap-x-2 gap-y-1 ${valueClass}`}>
        <span className="text-2xl font-bold tabular-nums sm:text-3xl">{mins.toLocaleString('fa-IR')}</span>
        <span className={unitClass}>دقیقه</span>
        {secs > 0 && (
          <>
            <span className="text-xl font-bold tabular-nums">{secs.toLocaleString('fa-IR')}</span>
            <span className={unitClass}>ثانیه</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-baseline gap-x-2 ${valueClass}`}>
      <span className="text-2xl font-bold tabular-nums sm:text-3xl">{secs.toLocaleString('fa-IR')}</span>
      <span className={unitClass}>ثانیه</span>
    </div>
  );
};

const formatDurationShort = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins.toLocaleString('fa-IR')} دقیقه`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0
    ? `${h.toLocaleString('fa-IR')} ساعت و ${m.toLocaleString('fa-IR')} دقیقه`
    : `${h.toLocaleString('fa-IR')} ساعت`;
};

type DatePreset = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days' | 'custom';

const SINGLE_DAY_PRESETS: { id: DatePreset; label: string; resolve: () => moment.Moment }[] = [
  { id: 'today', label: 'امروز', resolve: () => iranMoment() },
  { id: 'yesterday', label: 'دیروز', resolve: () => iranMoment().subtract(1, 'day') },
];

const RANGE_DAY_PRESETS: { id: DatePreset; label: string; days: number }[] = [
  { id: 'last_7_days', label: '۷ روز گذشته', days: 7 },
  { id: 'last_30_days', label: '۳۰ روز گذشته', days: 30 },
];

const ALL_PRESETS = [...SINGLE_DAY_PRESETS, ...RANGE_DAY_PRESETS];

const resolvePresetRange = (
  preset: DatePreset,
  customJalali: string
): { from: moment.Moment; to: moment.Moment } | null => {
  const single = SINGLE_DAY_PRESETS.find((p) => p.id === preset);
  if (single) {
    const day = single.resolve().startOf('day');
    return { from: day.clone(), to: day.clone() };
  }

  const range = RANGE_DAY_PRESETS.find((p) => p.id === preset);
  if (range) {
    const to = iranMoment().startOf('day');
    const from = to.clone().subtract(range.days - 1, 'days');
    return { from, to };
  }

  if (preset === 'custom') {
    const parts = customJalali.split('/');
    if (parts.length !== 3) return null;
    const jy = parseInt(parts[0], 10);
    const jm = parseInt(parts[1], 10);
    const jd = parseInt(parts[2], 10);
    if (Number.isNaN(jy) || Number.isNaN(jm) || Number.isNaN(jd)) return null;
    const day = moment().jYear(jy).jMonth(jm - 1).jDate(jd).startOf('day');
    if (!day.isValid()) return null;
    return { from: day.clone(), to: day.clone() };
  }

  return null;
};

const formatJalaliDay = (m: moment.Moment): string => toPersianDigits(m.format('jYYYY/jMM/jDD'));

interface CallCenterSummary {
  total_calls: number;
  inbound: number;
  outbound: number;
  unknown_direction: number;
  answered: number;
  no_answer: number;
  busy: number;
  failed: number;
  other_disposition: number;
  missed_inbound: number;
  answer_rate_pct: number;
  total_talk_seconds: number;
  avg_talk_seconds: number;
}

interface CallCenterDayRow {
  date: string;
  total: number;
  inbound: number;
  outbound: number;
  answered: number;
  missed: number;
}

interface CallCenterHourRow {
  hour: number;
  total: number;
  answered?: number;
  no_answer?: number;
  busy?: number;
  failed?: number;
  other?: number;
}

const HOUR_BAR_SEGMENTS = [
  { key: 'answered' as const, label: 'پاسخ داده', color: 'bg-emerald-500', fill: '#10b981' },
  { key: 'no_answer' as const, label: 'بدون پاسخ', color: 'bg-amber-500', fill: '#f59e0b' },
  { key: 'busy' as const, label: 'مشغول', color: 'bg-red-500', fill: '#ef4444' },
  { key: 'failed' as const, label: 'ناموفق', color: 'bg-violet-500', fill: '#8b5cf6' },
  { key: 'other' as const, label: 'سایر', color: 'bg-slate-400', fill: '#94a3b8' },
];

const HOURLY_CHART_BAR_AREA_PX = 200;
const HOURLY_CHART_Y_STEPS = 4;

type HourChartRow = {
  hour: number;
  total: number;
  answered: number;
  no_answer: number;
  busy: number;
  failed: number;
  other: number;
};

const niceChartMax = (value: number): number => {
  if (value <= 5) return 5;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  const nice =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
};

const HourlyDistributionChart: React.FC<{
  rows: HourChartRow[];
  peakHour: HourChartRow | null;
  isMultiDay?: boolean;
}> = ({ rows, peakHour, isMultiDay }) => {
  const [hoveredHour, setHoveredHour] = useState<number | null>(null);
  const [tooltipCoords, setTooltipCoords] = useState<{ top: number; left: number } | null>(null);
  const rawMax = Math.max(1, ...rows.map((row) => row.total));
  const yMax = niceChartMax(rawMax);
  const yTicks = useMemo(
    () =>
      Array.from({ length: HOURLY_CHART_Y_STEPS + 1 }, (_, index) =>
        Math.round((yMax * (HOURLY_CHART_Y_STEPS - index)) / HOURLY_CHART_Y_STEPS)
      ),
    [yMax]
  );
  const hoveredRow = hoveredHour != null ? rows.find((row) => row.hour === hoveredHour) : null;
  const isPeak = (hour: number, total: number) =>
    peakHour != null && peakHour.hour === hour && total > 0;

  const showBarTooltip = (hour: number, target: HTMLElement) => {
    const rect = target.getBoundingClientRect();
    setHoveredHour(hour);
    setTooltipCoords({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  };

  const hideBarTooltip = () => {
    setHoveredHour(null);
    setTooltipCoords(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {HOUR_BAR_SEGMENTS.map((segment) => (
            <span key={segment.key} className="inline-flex items-center gap-2 text-xs text-gray-600">
              <span
                className="h-3 w-3 rounded-[3px] shadow-sm"
                style={{ backgroundColor: segment.fill }}
                aria-hidden
              />
              {segment.label}
            </span>
          ))}
        </div>
        {peakHour && peakHour.total > 0 && (
          <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-800 ring-1 ring-indigo-100">
            <span className="h-2 w-2 rounded-full bg-indigo-500" aria-hidden />
            اوج: ساعت {peakHour.hour.toLocaleString('fa-IR')} —{' '}
            {peakHour.total.toLocaleString('fa-IR')} تماس
            {isMultiDay ? ' (مجموع بازه)' : ''}
          </span>
        )}
      </div>

      <div className="relative overflow-visible rounded-2xl border border-slate-200/80 bg-gradient-to-b from-slate-50 via-white to-white p-4 shadow-inner sm:p-5">
        <div className="flex gap-3">
          <div
            className="flex w-9 shrink-0 flex-col justify-between py-0.5 text-[10px] font-medium tabular-nums text-slate-400"
            style={{ height: HOURLY_CHART_BAR_AREA_PX }}
            aria-hidden
          >
            {yTicks.map((tick) => (
              <span key={tick}>{tick.toLocaleString('fa-IR')}</span>
            ))}
          </div>

          <div className="relative min-w-0 flex-1">
            <div
              className="pointer-events-none absolute inset-x-0 top-0 flex flex-col justify-between"
              style={{ height: HOURLY_CHART_BAR_AREA_PX }}
              aria-hidden
            >
              {yTicks.map((tick) => (
                <div key={tick} className="border-t border-slate-200/70" />
              ))}
            </div>

            <div
              className="relative flex items-end gap-[3px] overflow-visible border-b-2 border-slate-300/90 sm:gap-1"
              style={{ height: HOURLY_CHART_BAR_AREA_PX }}
              dir="rtl"
              onMouseLeave={hideBarTooltip}
            >
              {rows.map((row) => {
                const barHeight =
                  row.total > 0
                    ? Math.max(6, Math.round((row.total / yMax) * HOURLY_CHART_BAR_AREA_PX))
                    : 0;
                const peak = isPeak(row.hour, row.total);
                const active = hoveredHour === row.hour;

                return (
                  <div
                    key={row.hour}
                    className="group relative z-0 flex h-full min-w-0 flex-1 flex-col items-center justify-end hover:z-10"
                    onMouseEnter={(event) => showBarTooltip(row.hour, event.currentTarget)}
                  >
                    <div
                      className={`flex w-full max-w-[28px] flex-col items-center justify-end transition-transform duration-200 ${
                        active ? 'scale-105' : 'group-hover:scale-[1.03]'
                      }`}
                    >
                      {row.total === 0 ? (
                        <div
                          className="w-full rounded-t-sm bg-slate-100/80"
                          style={{ height: 4 }}
                        />
                      ) : (
                        <div
                          className="flex w-full flex-col-reverse justify-end overflow-hidden rounded-t-md shadow-sm"
                          style={{ height: barHeight }}
                        >
                          {(() => {
                            let usedHeight = 0;
                            const segments = HOUR_BAR_SEGMENTS.filter(
                              (segment) => row[segment.key] > 0
                            );
                            return segments.map((segment, index) => {
                              const count = row[segment.key];
                              const isLast = index === segments.length - 1;
                              const segmentHeight = isLast
                                ? Math.max(2, barHeight - usedHeight)
                                : Math.max(
                                    2,
                                    Math.round((count / row.total) * barHeight)
                                  );
                              usedHeight += segmentHeight;
                              return (
                                <div
                                  key={segment.key}
                                  className="w-full shrink-0"
                                  style={{
                                    height: segmentHeight,
                                    backgroundColor: segment.fill,
                                  }}
                                />
                              );
                            });
                          })()}
                        </div>
                      )}
                    </div>

                    <p
                      className={`mt-2 w-full text-center text-[10px] tabular-nums transition-colors sm:text-[11px] ${
                        peak
                          ? 'font-bold text-indigo-600'
                          : active
                            ? 'font-semibold text-slate-700'
                            : 'text-slate-400'
                      }`}
                    >
                      {row.hour.toLocaleString('fa-IR')}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {hoveredRow &&
          tooltipCoords &&
          hoveredRow.total > 0 &&
          createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed z-[9999] w-max max-w-[11rem] rounded-xl border border-slate-200 bg-white px-3 py-2 text-right shadow-xl ring-1 ring-black/5"
              style={{
                top: tooltipCoords.top,
                left: tooltipCoords.left,
                transform: 'translate(-50%, -100%)',
              }}
            >
              <p className="text-xs font-bold text-slate-900">
                ساعت {hoveredRow.hour.toLocaleString('fa-IR')}
              </p>
              <p className="mt-0.5 text-sm font-semibold tabular-nums text-indigo-700">
                {hoveredRow.total.toLocaleString('fa-IR')} تماس
              </p>
              <ul className="mt-2 space-y-1 border-t border-slate-100 pt-2">
                {HOUR_BAR_SEGMENTS.map((segment) => {
                  const count = hoveredRow[segment.key];
                  if (count <= 0) return null;
                  return (
                    <li
                      key={segment.key}
                      className="flex items-center justify-between gap-3 text-[11px] text-slate-600"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: segment.fill }}
                        />
                        {segment.label}
                      </span>
                      <span className="font-semibold tabular-nums text-slate-800">
                        {count.toLocaleString('fa-IR')}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

interface CallCenterOperatorRow {
  admin_id: number;
  username: string;
  extensions: string[];
  inbound: number;
  outbound: number;
  answered: number;
  inbound_answered: number;
  missed: number;
  talk_seconds: number;
  total: number;
  active_seconds?: number;
  rest_seconds?: number;
}

interface CallCenterExtensionRow {
  extension: string;
  agent_label: string;
  username?: string;
  admin_id?: number | null;
  inbound: number;
  outbound: number;
  answered: number;
  inbound_answered: number;
  missed: number;
  talk_seconds: number;
  total: number;
  active_seconds?: number;
  rest_seconds?: number;
}

interface OperatorPresenceSegment {
  type: 'active' | 'rest' | string;
  start: string;
  end: string;
  seconds: number;
}

interface OperatorCallRow {
  call_time: string;
  direction: string;
  direction_label: string;
  peer_number: string;
  extension?: string;
  disposition: string;
  disposition_label: string;
  billsec: number;
  unique_id?: string;
}

interface OperatorDetailReport {
  configured: boolean;
  message?: string | null;
  admin_id?: number | null;
  username: string;
  extensions: string[];
  extension?: string;
  agent_label?: string;
  inbound: number;
  outbound: number;
  answered: number;
  inbound_answered: number;
  missed: number;
  talk_seconds: number;
  total: number;
  answer_rate_pct: number;
  active_seconds: number;
  rest_seconds: number;
  presence_segments: OperatorPresenceSegment[];
  presence_events?: { event_type: string; created_at: string; queue_number?: string | null }[];
  calls: OperatorCallRow[];
  calls_total: number;
}

interface CallCenterDispositionRow {
  disposition: string;
  label: string;
  count: number;
}

interface CallCenterCacheStatus {
  initialized?: boolean;
  status?: string;
  records_cached?: number;
  cache_start_label?: string;
  last_sync_at?: string | null;
  sync_to?: string | null;
  message?: string | null;
  sync_running?: boolean;
  auto_scheduler_active?: boolean;
  auto_sync_interval_hours?: number;
  next_auto_sync_at?: string | null;
}

interface CallCenterReport {
  configured: boolean;
  message?: string | null;
  period_start: string;
  period_end: string;
  filtered_extension?: string | null;
  cdr_raw_rows?: number;
  cdr_unique_calls?: number;
  summary?: CallCenterSummary;
  by_disposition?: CallCenterDispositionRow[];
  by_day?: CallCenterDayRow[];
  by_hour?: CallCenterHourRow[];
  by_extension?: CallCenterExtensionRow[];
  by_operator?: CallCenterOperatorRow[];
  extension_options?: { extension: string; agent_label: string }[];
  data_source?: 'cache' | 'remote';
  cache_status?: CallCenterCacheStatus;
}

const METRIC_HINTS = {
  total_calls:
    'تماس‌های یکتا در بازه؛ رکوردهای تکراری CDR (مثلاً صف و اپراتور) یک‌بار شمرده می‌شوند.',
  answer_rate_pct:
    'پاسخ‌داده ÷ کل تماس‌های یکتا × ۱۰۰. بر اساس disposition برابر ANSWERED در CDR.',
  inbound: 'تماس‌هایی که مشتری یا خط خارجی زنگ زده (ورود به مرکز تماس).',
  outbound: 'تماس‌هایی که اپراتور از داخلی به بیرون زده است.',
  answered: 'تماس‌های یکتا با وضعیت ANSWERED — مکالمه برقرار شده است.',
  missed_inbound:
    'تماس ورودی با وضعیت NO ANSWER؛ زنگ خورده ولی پاسخ داده نشده (از دست رفته).',
  no_answer: 'همه تماس‌های بدون پاسخ (ورودی و خروجی) با disposition برابر NO ANSWER.',
  busy: 'تماس‌هایی که طرف مقابل مشغول بود و پاسخ نداد.',
  failed: 'تماس‌های ناموفق (FAILED) — قطع، خطا یا عدم برقراری تماس.',
  total_talk_seconds: 'مجموع مدت مکالمه (billsec) برای تماس‌های پاسخ‌داده‌شده.',
  avg_talk_seconds: 'میانگین مدت مکالمه برای تماس‌های پاسخ‌داده‌شده.',
} as const;

type MetricHintKey = keyof typeof METRIC_HINTS;

const HERO_METRICS: {
  key: MetricHintKey;
  label: string;
  sub?: string;
  bar: string;
  surface: string;
  valueClass: string;
  format?: (v: number) => string;
}[] = [
  {
    key: 'total_calls',
    label: 'کل تماس‌ها',
    sub: 'تماس یکتا',
    bar: 'bg-indigo-500',
    surface:
      'border-indigo-100/80 bg-gradient-to-br from-indigo-50/90 via-white to-white ring-indigo-100/60',
    valueClass: 'text-indigo-700',
  },
  {
    key: 'answer_rate_pct',
    label: 'نرخ پاسخگویی',
    bar: 'bg-emerald-500',
    surface:
      'border-emerald-100/80 bg-gradient-to-br from-emerald-50/90 via-white to-white ring-emerald-100/60',
    valueClass: 'text-emerald-700',
    format: (v) => `${v.toLocaleString('fa-IR', { maximumFractionDigits: 1 })}٪`,
  },
  {
    key: 'inbound',
    label: 'تماس ورودی',
    bar: 'bg-sky-500',
    surface:
      'border-sky-100/80 bg-gradient-to-br from-sky-50/90 via-white to-white ring-sky-100/60',
    valueClass: 'text-sky-700',
  },
  {
    key: 'outbound',
    label: 'تماس خروجی',
    bar: 'bg-violet-500',
    surface:
      'border-violet-100/80 bg-gradient-to-br from-violet-50/90 via-white to-white ring-violet-100/60',
    valueClass: 'text-violet-700',
  },
];

const SECONDARY_METRICS: {
  key: MetricHintKey;
  label: string;
  valueClass: string;
  bar: string;
  kind: 'count' | 'duration';
}[] = [
  { key: 'answered', label: 'پاسخ داده', valueClass: 'text-emerald-700', bar: 'bg-emerald-500', kind: 'count' },
  { key: 'missed_inbound', label: 'ورودی از دست رفته', valueClass: 'text-rose-700', bar: 'bg-rose-500', kind: 'count' },
  { key: 'no_answer', label: 'بدون پاسخ', valueClass: 'text-amber-700', bar: 'bg-amber-500', kind: 'count' },
  { key: 'busy', label: 'مشغول', valueClass: 'text-orange-700', bar: 'bg-orange-500', kind: 'count' },
  { key: 'failed', label: 'ناموفق', valueClass: 'text-red-700', bar: 'bg-red-500', kind: 'count' },
  {
    key: 'total_talk_seconds',
    label: 'مجموع مکالمه',
    valueClass: 'text-indigo-700',
    bar: 'bg-indigo-500',
    kind: 'duration',
  },
  {
    key: 'avg_talk_seconds',
    label: 'میانگین مکالمه',
    valueClass: 'text-violet-700',
    bar: 'bg-violet-500',
    kind: 'duration',
  },
];

const SECONDARY_COUNT_METRICS = SECONDARY_METRICS.filter((metric) => metric.kind === 'count');
const SECONDARY_DURATION_METRICS = SECONDARY_METRICS.filter((metric) => metric.kind === 'duration');

const cardClass = 'rounded-xl border border-gray-200 bg-white shadow-sm';

const DISPOSITION_COLORS: Record<string, string> = {
  ANSWERED: 'bg-emerald-500',
  'NO ANSWER': 'bg-amber-400',
  BUSY: 'bg-orange-400',
  FAILED: 'bg-rose-500',
  CONGESTION: 'bg-slate-400',
};

type TooltipPlacement = 'above' | 'below';

type TooltipCoords = {
  top: number;
  right?: number;
  left?: number;
  placement: TooltipPlacement;
};

const tooltipBubbleClass =
  'pointer-events-none fixed z-[9999] w-max max-w-[min(18rem,calc(100vw-2rem))] rounded-lg bg-gray-900 px-2.5 py-2 text-right text-[11px] font-normal leading-relaxed text-white shadow-lg';

const tooltipPositionStyle = (coords: TooltipCoords): React.CSSProperties => {
  if (coords.left != null) {
    return {
      top: coords.placement === 'below' ? coords.top : undefined,
      bottom: coords.placement === 'above' ? window.innerHeight - coords.top : undefined,
      left: coords.left,
      transform:
        coords.placement === 'above' ? 'translate(-50%, -100%)' : 'translateX(-50%)',
    };
  }
  return {
    top: coords.placement === 'below' ? coords.top : undefined,
    bottom: coords.placement === 'above' ? window.innerHeight - coords.top : undefined,
    right: coords.right,
    transform: coords.placement === 'above' ? 'translateY(-100%)' : undefined,
  };
};

const TooltipInfoIcon: React.FC = () => (
  <span
    className="inline-flex h-4 min-w-[1rem] shrink-0 select-none items-center justify-center rounded-full border border-gray-200 bg-gray-50 px-1 text-[8px] font-bold leading-none text-gray-500"
    aria-hidden
  >
    i
  </span>
);

const renderTooltipPortal = (hint: string, coords: TooltipCoords | null, align: 'right' | 'center' = 'right') =>
  coords &&
  createPortal(
    <span
      role="tooltip"
      aria-hidden="true"
      className={`${tooltipBubbleClass}${align === 'center' ? ' text-center' : ''}`}
      style={tooltipPositionStyle(coords)}
    >
      {hint}
    </span>,
    document.body
  );

const useTooltipPortal = (placement: TooltipPlacement = 'below', align: 'right' | 'center' = 'right') => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);

  const showTooltip = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (align === 'center') {
      setCoords({
        top: placement === 'below' ? rect.bottom + 6 : rect.top - 6,
        left: rect.left + rect.width / 2,
        placement,
      });
      return;
    }
    setCoords({
      top: placement === 'below' ? rect.bottom + 6 : rect.top - 6,
      right: window.innerWidth - rect.right,
      placement,
    });
  }, [align, placement]);

  const hideTooltip = useCallback(() => setCoords(null), []);

  const triggerHandlers = {
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip,
  };

  return { triggerRef, triggerHandlers, coords };
};

const HintTooltip: React.FC<{
  hint: string;
  children: React.ReactNode;
  placement?: TooltipPlacement;
  className?: string;
}> = ({ hint, children, placement = 'below', className = '' }) => {
  const hintId = useId().replace(/:/g, '');
  const { triggerRef, triggerHandlers, coords } = useTooltipPortal(placement);

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex cursor-help items-center gap-1 ${className}`}
        aria-describedby={hintId}
        {...triggerHandlers}
      >
        {children}
        <span id={hintId} className="sr-only">
          {hint}
        </span>
      </span>
      {renderTooltipPortal(hint, coords)}
    </>
  );
};

const TableHeaderWithTooltip: React.FC<{ label: string; hint: string; className?: string }> = ({
  label,
  hint,
  className = '',
}) => {
  const hintId = useId().replace(/:/g, '');
  const { triggerRef, triggerHandlers, coords } = useTooltipPortal('above', 'center');

  return (
    <th className={`px-4 py-3 text-right font-semibold ${className}`}>
      <span
        ref={triggerRef}
        className="inline-flex cursor-help items-center gap-1.5 rounded outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1"
        tabIndex={0}
        aria-describedby={hintId}
        {...triggerHandlers}
      >
        <span>{label}</span>
        <TooltipInfoIcon />
        <span id={hintId} className="sr-only">
          {hint}
        </span>
      </span>
      {renderTooltipPortal(hint, coords, 'center')}
    </th>
  );
};

const PAGE_HINTS = {
  dateRange:
    'بازهٔ زمانی گزارش. می‌توانید امروز، دیروز، ۷ یا ۳۰ روز گذشته را بزنید یا از تقویم یک روز مشخص انتخاب کنید.',
  calendar:
    'انتخاب یک روز مشخص در تقویم شمسی. برای بازهٔ ۷ یا ۳۰ روزه از دکمه‌های بالا استفاده کنید.',
  cacheLocal:
    'داده‌های CDR در کش محلی ذخیره شده‌اند. «تا تاریخ …» یعنی آخرین روزی که تماس‌ها در کش موجود است. همگام‌سازی خودکار هر ۶ ساعت است؛ اگر سرور خاموش بوده، با روشن شدن خودش عقب‌افتادگی را جبران می‌کند.',
  cdrDedup:
    'Asterisk برای هر تماس ممکن است چند رکورد CDR بسازد (صف، IVR، اپراتور). گزارش آن‌ها را یک تماس واحد می‌شمارد.',
  dailyTrend: 'تعداد تماس یکتا در هر روز از بازهٔ انتخاب‌شده (مثلاً ۷ یا ۳۰ روز).',
  hourlyChart:
    'تعداد تماس یکتا در هر ساعت (۰ تا ۲۳). هر میله با رنگ‌های مختلف وضعیت تماس (پاسخ، بدون پاسخ، …) را نشان می‌دهد.',
  dispositionSection:
    'تفکیک تماس‌های یکتا بر اساس وضعیت نهایی (disposition) در CDR.',
  operatorsSection:
    'آمار هر اپراتور با داخلی ۲ رقمی. تماس‌های صف به اپراتور پاسخ‌دهنده نسبت داده می‌شود.',
} as const;

const DISPOSITION_HINTS: Record<string, string> = {
  ANSWERED: 'تماس برقرار شده و پاسخ داده شده است.',
  'NO ANSWER': 'زنگ خورد ولی کسی پاسخ نداد یا زمان انتظار تمام شد.',
  BUSY: 'خط یا داخلی مقصد مشغول بود.',
  FAILED: 'تماس برقرار نشد؛ خطا، قطع زودهنگام یا مشکل فنی.',
  CONGESTION: 'شبکه یا خط اشغال بود و تماس برقرار نشد.',
};

const OPERATOR_COLUMN_HINTS = {
  rank: 'رتب‌بندی بر اساس تعداد کل تماس‌های نسبت‌داده‌شده به کاربر.',
  operator: 'نام کاربری پنل و داخلی‌هایی که در این بازه با آن‌ها لاگین کرده است.',
  inbound: 'مجموع تماس‌های ورودی روی داخلی‌های این کاربر.',
  outbound: 'مجموع تماس‌های خروجی از داخلی‌های این کاربر.',
  inboundAnswered: 'تماس‌های ورودی پاسخ‌داده‌شده.',
  missed: 'تماس ورودی بدون پاسخ.',
  talkSeconds: 'مجموع زمان مکالمه برای تماس‌های پاسخ‌داده.',
  answerRate: 'پاسخ ورودی ÷ کل ورودی × ۱۰۰.',
  activeSeconds: 'مدت حضور در صف نوبت‌دهی.',
  restSeconds: 'مدت استراحت (خارج از صف).',
} as const;

const PRESET_HINTS: Record<DatePreset, string> = {
  today: 'گزارش فقط برای امروز (تاریخ امروز ایران).',
  yesterday: 'گزارش فقط برای دیروز.',
  last_7_days: 'گزارش از ۷ روز گذشته تا امروز (۷ روز متوالی).',
  last_30_days: 'گزارش از ۳۰ روز گذشته تا امروز (۳۰ روز متوالی).',
  custom: 'روزی که از تقویم شمسی انتخاب کرده‌اید.',
};

const formatCacheSyncToFa = (syncTo: string | null | undefined): string | null => {
  if (!syncTo) return null;
  const trimmed = syncTo.trim().slice(0, 10);
  const m = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
    ? moment(new Date(`${trimmed}T12:00:00`))
    : moment(new Date(syncTo));
  return m.isValid() ? toPersianDigits(m.format('jYYYY/jMM/jDD')) : null;
};

const buildCacheReadyMessage = (
  report: CallCenterReport
): { text: string; tone: 'emerald' | 'amber' | 'indigo' } | null => {
  const cs = report.cache_status;
  if (!cs) return null;

  const syncToFa = formatCacheSyncToFa(cs.sync_to);
  const records = cs.records_cached ?? 0;
  const lastSync = cs.last_sync_at
    ? new Date(cs.last_sync_at).toLocaleString('fa-IR')
    : null;
  const nextSync = cs.next_auto_sync_at
    ? new Date(cs.next_auto_sync_at).toLocaleString('fa-IR')
    : null;
  const hasUsableCache = records > 0 && Boolean(syncToFa);
  const schedulerNote = cs.auto_scheduler_active
    ? nextSync
      ? `— همگام‌سازی بعدی: ${nextSync}`
      : '— همگام‌سازی خودکار فعال است'
    : '— همگام‌سازی خودکار هنوز فعال نشده (بک‌اند را ریستارت کنید)';

  if (cs.sync_running || cs.status === 'running') {
    return {
      tone: 'amber',
      text: 'در حال همگام‌سازی خودکار… چند لحظه دیگر رفرش کنید.',
    };
  }

  if (hasUsableCache) {
    const parts = [`داده تا ${syncToFa} آماده است`];
    if (lastSync) parts.push(`— آخرین همگام‌سازی: ${lastSync}`);
    parts.push(schedulerNote);
    if (report.data_source === 'remote') {
      parts.push('— این گزارش مستقیم از Issabel خوانده شد');
    }
    return { tone: 'emerald', text: parts.join(' ') };
  }

  if (report.data_source === 'remote') {
    return {
      tone: 'amber',
      text: 'گزارش از Issabel خوانده شد. کش محلی برای این بازه در دسترس نبود.',
    };
  }

  return {
    tone: 'amber',
    text: `داده هنوز آماده نیست. همگام‌سازی خودکار هر ۶ ساعت + جبران هنگام روشن شدن سرور. ${schedulerNote}`,
  };
};

const detectSingleDayPreset = (jalaliDate: string): DatePreset => {
  for (const preset of SINGLE_DAY_PRESETS) {
    if (preset.resolve().format('jYYYY/jMM/jDD') === jalaliDate) return preset.id;
  }
  return 'custom';
};

const ReportLoadingOverlay: React.FC<{ open: boolean }> = ({ open }) => {
  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/35 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-busy="true"
      aria-label="در حال بارگذاری گزارش"
    >
      <div className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl shadow-indigo-900/10 ring-1 ring-indigo-100/80">
        <div
          className="h-1 bg-gradient-to-l from-indigo-500 via-violet-500 to-indigo-600"
          aria-hidden
        />
        <div className="pointer-events-none absolute -left-10 -top-10 h-32 w-32 rounded-full bg-indigo-100/70 blur-2xl" aria-hidden />
        <div className="pointer-events-none absolute -bottom-8 -right-8 h-28 w-28 rounded-full bg-violet-100/60 blur-2xl" aria-hidden />

        <div className="relative flex items-center gap-5 px-7 py-6">
          <div className="relative h-14 w-14 shrink-0" aria-hidden>
            <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-indigo-100 border-t-indigo-600" />
            <span className="absolute inset-2 animate-ping rounded-full bg-indigo-400/25" />
            <span className="absolute inset-0 flex items-center justify-center text-indigo-600">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.75}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-gray-900">در حال بارگذاری گزارش</p>
            <p className="mt-1 text-sm text-gray-500">دریافت آمار تماس‌ها</p>
            <span className="mt-3 inline-flex items-center gap-1.5" aria-hidden>
              {[0, 1, 2].map((index) => (
                <span
                  key={index}
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-indigo-500"
                  style={{ animationDelay: `${index * 140}ms` }}
                />
              ))}
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const AdminCallCenterReports: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(() => iranMoment().format('jYYYY/jMM/jDD'));
  const [activePreset, setActivePreset] = useState<DatePreset>('today');
  const [report, setReport] = useState<CallCenterReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMainAdmin = useMemo(
    () => (localStorage.getItem('admin_role') || '').trim().toLowerCase() === 'admin',
    []
  );
  const [showResyncPanel, setShowResyncPanel] = useState(false);
  const [resyncFrom, setResyncFrom] = useState(() => iranMoment().format('jYYYY/jMM/jDD'));
  const [resyncTo, setResyncTo] = useState(() => iranMoment().format('jYYYY/jMM/jDD'));
  const [resyncing, setResyncing] = useState(false);
  const [resyncMsg, setResyncMsg] = useState<{ tone: 'emerald' | 'amber' | 'red'; text: string } | null>(
    null
  );
  const resyncPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [selectedOperatorAdminId, setSelectedOperatorAdminId] = useState<number | null>(null);
  const [operatorDetail, setOperatorDetail] = useState<OperatorDetailReport | null>(null);
  const [operatorDetailLoading, setOperatorDetailLoading] = useState(false);
  const [operatorDetailError, setOperatorDetailError] = useState<string | null>(null);
  const [operatorCallsPage, setOperatorCallsPage] = useState(1);
  const OPERATOR_CALLS_PAGE_SIZE = 10;

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (!canAccessCallCenterReport()) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  useEffect(
    () => () => {
      if (resyncPollRef.current) clearInterval(resyncPollRef.current);
    },
    []
  );

  const applyPreset = (preset: DatePreset) => {
    setActivePreset(preset);
    if (preset === 'today' || preset === 'yesterday') {
      const found = SINGLE_DAY_PRESETS.find((p) => p.id === preset);
      if (found) setSelectedDate(found.resolve().format('jYYYY/jMM/jDD'));
    }
  };

  const handleCalendarChange = (jalali: string) => {
    setSelectedDate(jalali);
    setActivePreset(detectSingleDayPreset(jalali));
  };

  const dateRange = useMemo(() => {
    const resolved = resolvePresetRange(activePreset, selectedDate);
    if (!resolved) return null;
    const { from, to } = resolved;
    const fromGregorian = from.format('YYYY-MM-DD');
    const toGregorian = to.format('YYYY-MM-DD');
    const dayCount = to.diff(from, 'days') + 1;
    const isMultiDay = dayCount > 1;
    const label = isMultiDay
      ? `از ${formatJalaliDay(from)} تا ${formatJalaliDay(to)} (${dayCount.toLocaleString('fa-IR')} روز)`
      : formatJalaliDay(from);
    return { fromGregorian, toGregorian, label, dayCount, isMultiDay };
  }, [activePreset, selectedDate]);

  const openOperatorDetail = useCallback(
    async (adminId: number) => {
      if (!dateRange) return;
      setSelectedOperatorAdminId(adminId);
      setOperatorDetail(null);
      setOperatorDetailError(null);
      setOperatorDetailLoading(true);
      setOperatorCallsPage(1);
      try {
        const token = localStorage.getItem('admin_token');
        const apiBaseUrl = getApiBaseUrl();
        const params = new URLSearchParams({
          from_date: dateRange.fromGregorian,
          to_date: dateRange.toGregorian,
          admin_id: String(adminId),
        });
        const response = await fetch(
          `${apiBaseUrl}/admin/reports/call-center/operator-details?${params}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.detail || 'خطا در دریافت جزئیات اپراتور');
        }
        const data: OperatorDetailReport = await response.json();
        if (!data.configured) {
          throw new Error(data.message || 'گزارش پیکربندی نشده است.');
        }
        setOperatorDetail(data);
      } catch (err: any) {
        setOperatorDetailError(err?.message || 'خطا در دریافت جزئیات');
      } finally {
        setOperatorDetailLoading(false);
      }
    },
    [dateRange]
  );

  const closeOperatorDetail = () => {
    setSelectedOperatorAdminId(null);
    setOperatorDetail(null);
    setOperatorDetailError(null);
    setOperatorDetailLoading(false);
    setOperatorCallsPage(1);
  };

  const fetchReport = useCallback(async () => {
    if (!dateRange) {
      setError('تاریخ انتخاب‌شده معتبر نیست.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const params = new URLSearchParams({
        from_date: dateRange.fromGregorian,
        to_date: dateRange.toGregorian,
      });
      const response = await fetch(`${apiBaseUrl}/admin/reports/call-center?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const detail = body.detail;
        const msg =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d: any) => d?.msg || JSON.stringify(d)).join(' — ')
              : 'خطا در دریافت گزارش مرکز تماس';
        throw new Error(msg);
      }
      const data: CallCenterReport = await response.json();
      setReport(data);
      if (!data.configured && data.message) {
        setError(data.message);
      }
    } catch (err: unknown) {
      setReport(null);
      setError(err instanceof Error ? err.message : 'خطا در دریافت گزارش');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  const handleManualResync = useCallback(async () => {
    const fromGregorian = jalaliToGregorianYmd(resyncFrom);
    const toGregorian = jalaliToGregorianYmd(resyncTo);
    if (!fromGregorian || !toGregorian) {
      setResyncMsg({ tone: 'red', text: 'تاریخ‌های انتخاب‌شده معتبر نیستند.' });
      return;
    }
    if (fromGregorian > toGregorian) {
      setResyncMsg({ tone: 'red', text: 'تاریخ شروع نباید بعد از تاریخ پایان باشد.' });
      return;
    }

    setResyncing(true);
    setResyncMsg({ tone: 'amber', text: 'در حال شروع بازخوانی…' });
    try {
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const params = new URLSearchParams({ from_date: fromGregorian, to_date: toGregorian });
      const response = await fetch(
        `${apiBaseUrl}/admin/reports/call-center/resync-range?${params}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'شروع بازخوانی ناموفق بود');
      }
      const data = await response.json();
      setResyncMsg({
        tone: 'amber',
        text: data.message || 'بازخوانی در پس‌زمینه شروع شد. در حال بررسی وضعیت…',
      });

      if (resyncPollRef.current) clearInterval(resyncPollRef.current);
      resyncPollRef.current = setInterval(async () => {
        try {
          const statusRes = await fetch(
            `${apiBaseUrl}/admin/reports/call-center/cache-status`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );
          if (!statusRes.ok) return;
          const status: CallCenterCacheStatus = await statusRes.json();
          if (status.status !== 'running') {
            if (resyncPollRef.current) {
              clearInterval(resyncPollRef.current);
              resyncPollRef.current = null;
            }
            setResyncing(false);
            setResyncMsg({
              tone: 'emerald',
              text: status.message || 'بازخوانی کش با موفقیت انجام شد.',
            });
            void fetchReport();
          }
        } catch {
          /* ignore transient polling errors */
        }
      }, 3000);
    } catch (err: unknown) {
      setResyncing(false);
      setResyncMsg({
        tone: 'red',
        text: err instanceof Error ? err.message : 'خطا در بازخوانی دستی',
      });
    }
  }, [resyncFrom, resyncTo, fetchReport]);

  const summary = report?.summary;

  const hourRows = useMemo(() => {
    const rows = report?.by_hour || [];
    const map = new Map(rows.map((r) => [r.hour, r]));
    return Array.from({ length: 24 }, (_, hour) => {
      const row = map.get(hour);
      return {
        hour,
        total: row?.total ?? 0,
        answered: row?.answered ?? 0,
        no_answer: row?.no_answer ?? 0,
        busy: row?.busy ?? 0,
        failed: row?.failed ?? 0,
        other: row?.other ?? 0,
      };
    });
  }, [report?.by_hour]);

  const peakHour = useMemo(() => {
    const peak = hourRows.reduce((best, row) => (row.total > best.total ? row : best), hourRows[0]);
    return peak?.total ? peak : null;
  }, [hourRows]);

  const dispositionTotal = useMemo(
    () => (report?.by_disposition || []).reduce((s, d) => s + d.count, 0),
    [report?.by_disposition]
  );

  const topOperators = useMemo(() => {
    const rows = report?.by_operator || [];
    return rows.map((row, index) => ({
      ...row,
      rank: index + 1,
      answerPct:
        row.inbound > 0
          ? Math.round(((row.inbound_answered ?? 0) / row.inbound) * 100)
          : 0,
    }));
  }, [report?.by_operator]);

  const dayRows = useMemo(() => {
    const rows = report?.by_day || [];
    if (!dateRange) return rows;
    return rows.filter((d) => {
      const day = d.date.trim().slice(0, 10);
      return day >= dateRange.fromGregorian && day <= dateRange.toGregorian;
    });
  }, [report?.by_day, dateRange]);
  const maxDayTotal = useMemo(
    () => Math.max(1, ...dayRows.map((d) => d.total)),
    [dayRows]
  );

  const formatDayChartLabel = (dayStr: string): string => {
    const trimmed = dayStr.trim().slice(0, 10);
    const m = /^\d{4}-\d{2}-\d{2}$/.test(trimmed)
      ? moment(new Date(`${trimmed}T12:00:00`))
      : moment(dayStr);
    return m.isValid() ? toPersianDigits(m.format('jMM/jDD')) : dayStr;
  };

  return (
    <div className="space-y-6 text-base text-gray-900" dir="rtl">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-bl from-indigo-600 via-indigo-700 to-slate-800 px-6 py-7 text-white shadow-xl sm:px-8">
        <div className="pointer-events-none absolute -left-16 -top-16 h-52 w-52 rounded-full bg-white/10 blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -bottom-12 -right-8 h-40 w-40 rounded-full bg-indigo-400/25 blur-2xl" aria-hidden />
        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur-sm">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-300" aria-hidden />
              CDR Issabel · {dateRange?.isMultiDay ? `گزارش ${dateRange.dayCount.toLocaleString('fa-IR')} روزه` : 'گزارش روزانه'}
            </div>
            <h1 className="text-2xl font-bold sm:text-3xl">گزارش مرکز تماس</h1>
            <p className="mt-2 text-sm text-indigo-100/90">
              آمار تماس‌های ورودی و خروجی — {dateRange?.label ?? '—'}
            </p>
          </div>
          {summary && (
            <div className="flex flex-wrap gap-3">
              <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/20">
                <p className="text-xs text-indigo-100">تماس یکتا</p>
                <p className="mt-1 text-2xl font-bold tabular-nums">
                  {summary.total_calls.toLocaleString('fa-IR')}
                </p>
              </div>
              <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/20">
                <p className="text-xs text-indigo-100">نرخ پاسخ</p>
                <p className="mt-1 text-2xl font-bold tabular-nums text-emerald-300">
                  {summary.answer_rate_pct.toLocaleString('fa-IR', { maximumFractionDigits: 1 })}٪
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <section className="rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm ring-1 ring-gray-100/80">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <HintTooltip hint={PAGE_HINTS.dateRange} placement="below">
            <span className="shrink-0 text-sm font-semibold text-gray-800">
              انتخاب بازه
              <TooltipInfoIcon />
            </span>
          </HintTooltip>

          <div className="flex flex-wrap items-center gap-1.5">
            {ALL_PRESETS.map((preset) => {
              const isActive = activePreset === preset.id;
              return (
                <HintTooltip key={preset.id} hint={PRESET_HINTS[preset.id]} placement="below">
                  <button
                    type="button"
                    onClick={() => applyPreset(preset.id)}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'border border-gray-200 bg-white text-gray-700 hover:border-indigo-200 hover:bg-indigo-50/50'
                    }`}
                  >
                    {preset.label}
                  </button>
                </HintTooltip>
              );
            })}
          </div>

          <div className="mr-auto flex min-w-[11rem] items-center gap-2 sm:min-w-[13rem]">
            <HintTooltip hint={PAGE_HINTS.calendar} placement="below">
              <label className="shrink-0 text-xs font-medium text-gray-500">
                تقویم
                <TooltipInfoIcon />
              </label>
            </HintTooltip>
            <div className="min-w-0 flex-1">
              <PersianDatePicker
                value={selectedDate}
                onChange={handleCalendarChange}
                placeholder="انتخاب تاریخ"
              />
            </div>
          </div>
        </div>

        {report && (() => {
          const cacheMsg = buildCacheReadyMessage(report);
          if (!cacheMsg) return null;
          const toneClass =
            cacheMsg.tone === 'emerald'
              ? 'text-emerald-800 bg-emerald-50 border-emerald-100'
              : cacheMsg.tone === 'indigo'
                ? 'text-indigo-800 bg-indigo-50 border-indigo-100'
                : 'text-amber-800 bg-amber-50 border-amber-100';
          return (
            <p className={`mt-2 rounded-lg border px-3 py-1.5 text-xs leading-relaxed ${toneClass}`}>
              <HintTooltip hint={PAGE_HINTS.cacheLocal} placement="below">
                <span>
                  {cacheMsg.text}
                  <TooltipInfoIcon />
                </span>
              </HintTooltip>
            </p>
          );
        })()}
      </section>

      {isMainAdmin && (
        <section className="rounded-xl border border-amber-200 bg-amber-50/40 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </span>
              <div>
                <p className="text-sm font-bold text-gray-900">بازخوانی دستی کش (فقط مدیر اصلی)</p>
                <p className="text-xs text-gray-500">
                  اگر شمارش روزهایی نادرست است، بازهٔ آن روزها را دوباره از Issabel بخوانید.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowResyncPanel((v) => !v)}
              className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-100"
            >
              {showResyncPanel ? 'بستن' : 'بازخوانی دستی'}
            </button>
          </div>

          {showResyncPanel && (
            <div className="border-t border-amber-200 px-4 py-4">
              <div className="flex flex-wrap items-end gap-4">
                <div className="min-w-[10rem]">
                  <label className="mb-1 block text-xs font-medium text-gray-600">از تاریخ</label>
                  <PersianDatePicker
                    value={resyncFrom}
                    onChange={setResyncFrom}
                    placeholder="از تاریخ"
                  />
                </div>
                <div className="min-w-[10rem]">
                  <label className="mb-1 block text-xs font-medium text-gray-600">تا تاریخ</label>
                  <PersianDatePicker
                    value={resyncTo}
                    onChange={setResyncTo}
                    placeholder="تا تاریخ"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void handleManualResync()}
                  disabled={resyncing}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {resyncing && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  )}
                  {resyncing ? 'در حال بازخوانی…' : 'شروع بازخوانی این بازه'}
                </button>
              </div>

              <p className="mt-3 text-xs leading-relaxed text-gray-500">
                رکوردهای این بازه در کش محلی حذف و از نو از Issabel دریافت می‌شوند. این کار روی
                بقیهٔ روزها اثری ندارد و ممکن است چند لحظه طول بکشد.
              </p>

              {resyncMsg && (
                <p
                  className={`mt-3 rounded-lg border px-3 py-2 text-xs leading-relaxed ${
                    resyncMsg.tone === 'emerald'
                      ? 'border-emerald-100 bg-emerald-50 text-emerald-800'
                      : resyncMsg.tone === 'red'
                        ? 'border-red-100 bg-red-50 text-red-800'
                        : 'border-amber-100 bg-amber-50 text-amber-800'
                  }`}
                >
                  {resyncMsg.text}
                </p>
              )}
            </div>
          )}
        </section>
      )}

      <ReportLoadingOverlay open={loading} />

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-900">
          {error}
        </div>
      )}

      {report?.configured && summary ? (
        <div className="space-y-6">
            {typeof report.cdr_raw_rows === 'number' &&
              typeof report.cdr_unique_calls === 'number' &&
              report.cdr_raw_rows > report.cdr_unique_calls && (
                <div className="flex items-center gap-2 rounded-lg border border-sky-100 bg-sky-50/80 px-3 py-1.5 text-xs leading-relaxed text-sky-900">
                  <HintTooltip hint={PAGE_HINTS.cdrDedup} placement="below">
                    <span className="flex h-5 w-5 shrink-0 cursor-help items-center justify-center rounded-full bg-sky-100 text-sky-600">
                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </span>
                  </HintTooltip>
                  <p>
                    Asterisk برای هر تماس چند رکورد CDR می‌سازد. این گزارش{' '}
                    <strong>{report.cdr_unique_calls.toLocaleString('fa-IR')} تماس یکتا</strong>{' '}
                    را از {report.cdr_raw_rows.toLocaleString('fa-IR')} رکورد خام محاسبه کرده است.
                  </p>
                </div>
              )}

            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
              {HERO_METRICS.map((metric) => {
                const raw = Number(summary[metric.key] ?? 0);
                const display = metric.format
                  ? metric.format(raw)
                  : raw.toLocaleString('fa-IR');
                return (
                  <div
                    key={metric.key}
                    className={`relative overflow-hidden rounded-2xl border p-5 text-right shadow-md shadow-gray-200/30 ring-1 sm:p-6 ${metric.surface}`}
                  >
                    <div className={`absolute inset-x-0 top-0 h-1 ${metric.bar}`} aria-hidden />
                    <HintTooltip hint={METRIC_HINTS[metric.key]} placement="above">
                      <p className="text-sm font-medium text-gray-600">
                        {metric.label}
                        <TooltipInfoIcon />
                      </p>
                    </HintTooltip>
                    {metric.sub && (
                      <p className="mt-0.5 text-xs text-gray-400">{metric.sub}</p>
                    )}
                    <p className={`mt-3 text-3xl font-bold tabular-nums tracking-tight sm:text-4xl ${metric.valueClass}`}>
                      {display}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm ring-1 ring-gray-100/80">
              <div className="border-b border-gray-100 px-5 py-4 sm:px-6">
                <HintTooltip
                  hint="شاخص‌های تکمیلی تماس در بازهٔ انتخاب‌شده"
                  placement="below"
                >
                  <h2 className="text-base font-bold text-gray-900">
                    جزئیات وضعیت تماس
                    <TooltipInfoIcon />
                  </h2>
                </HintTooltip>
              </div>

              <div className="space-y-4 p-5 sm:p-6">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  {SECONDARY_COUNT_METRICS.map((metric) => {
                    const raw = Number(summary[metric.key] ?? 0);
                    return (
                      <div
                        key={metric.key}
                        className="relative overflow-hidden rounded-xl border border-gray-100 bg-gray-50/50 p-4"
                      >
                        <div className={`absolute inset-x-0 top-0 h-0.5 ${metric.bar}`} aria-hidden />
                        <HintTooltip hint={METRIC_HINTS[metric.key]} placement="above">
                          <p className="text-sm font-medium text-gray-600">
                            {metric.label}
                            <TooltipInfoIcon />
                          </p>
                        </HintTooltip>
                        <p className={`mt-2 text-3xl font-bold tabular-nums tracking-tight ${metric.valueClass}`}>
                          {raw.toLocaleString('fa-IR')}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {SECONDARY_DURATION_METRICS.map((metric) => {
                    const raw = Number(summary[metric.key] ?? 0);
                    return (
                      <div
                        key={metric.key}
                        className="relative overflow-hidden rounded-xl border border-gray-100 bg-gray-50/50 p-4 sm:p-5"
                      >
                        <div className={`absolute inset-x-0 top-0 h-0.5 ${metric.bar}`} aria-hidden />
                        <HintTooltip hint={METRIC_HINTS[metric.key]} placement="above">
                          <p className="text-sm font-medium text-gray-600">
                            {metric.label}
                            <TooltipInfoIcon />
                          </p>
                        </HintTooltip>
                        <div className="mt-3">
                          <DurationMetricValue seconds={raw} valueClass={metric.valueClass} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {dateRange?.isMultiDay && dayRows.length > 0 && (
              <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md shadow-gray-200/30 ring-1 ring-gray-100/80">
                <div className="border-b border-gray-100 bg-gradient-to-l from-slate-50/80 to-white px-5 py-5 sm:px-6">
                  <HintTooltip hint={PAGE_HINTS.dailyTrend} placement="below">
                    <h2 className="text-xl font-bold text-gray-900">
                      روند روزانه
                      <TooltipInfoIcon />
                    </h2>
                  </HintTooltip>
                  <p className="mt-1.5 text-sm text-gray-500">
                    تعداد تماس یکتا در هر روز از بازهٔ {dateRange.dayCount.toLocaleString('fa-IR')} روزه
                  </p>
                </div>
                <div className="overflow-x-auto px-5 py-5 sm:px-6">
                  <div className="flex min-w-max snap-x snap-mandatory items-end gap-3 pb-1">
                    {dayRows.map((day) => {
                      const barHeight = Math.max(
                        4,
                        Math.round((day.total / maxDayTotal) * 160)
                      );
                      return (
                        <div key={day.date} className="flex w-11 shrink-0 snap-start flex-col items-center">
                          <div
                            className="flex w-full flex-col items-center justify-end border-b border-gray-300/90"
                            style={{ height: 160 }}
                          >
                            <div
                              className="w-8 rounded-t-md bg-gradient-to-t from-indigo-600 to-indigo-400 shadow-sm sm:w-9"
                              style={{ height: barHeight }}
                              title={`${formatDayChartLabel(day.date)} — ${day.total.toLocaleString('fa-IR')} تماس`}
                            />
                          </div>
                          <p className="mt-2 text-[11px] text-gray-600">{formatDayChartLabel(day.date)}</p>
                          <p className="mt-0.5 text-sm font-semibold tabular-nums text-gray-900">
                            {day.total.toLocaleString('fa-IR')}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
              <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md shadow-gray-200/30 ring-1 ring-gray-100/80 xl:col-span-2">
                <div className="border-b border-gray-100 bg-gradient-to-l from-slate-50/80 to-white px-5 py-5 sm:px-6">
                  <HintTooltip hint={PAGE_HINTS.hourlyChart} placement="below">
                    <h2 className="text-xl font-bold text-gray-900">
                      توزیع ساعتی تماس‌ها
                      <TooltipInfoIcon />
                    </h2>
                  </HintTooltip>
                  <p className="mt-1.5 text-sm text-gray-500">
                    {peakHour
                      ? `شلوغ‌ترین ساعت: ${peakHour.hour.toLocaleString('fa-IR')} با ${peakHour.total.toLocaleString('fa-IR')} تماس${
                          dateRange?.isMultiDay ? ' (مجموع کل بازه)' : ''
                        }`
                      : dateRange?.isMultiDay
                        ? 'بدون تماس در این بازه'
                        : 'بدون تماس در این روز'}
                  </p>
                </div>
                <div className="p-5 sm:p-6">
                  <HourlyDistributionChart
                    rows={hourRows}
                    peakHour={peakHour}
                    isMultiDay={dateRange?.isMultiDay}
                  />
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md shadow-gray-200/30 ring-1 ring-gray-100/80">
                <div className="border-b border-gray-100 bg-gradient-to-l from-slate-50/80 to-white px-5 py-5 sm:px-6">
                  <HintTooltip hint={PAGE_HINTS.dispositionSection} placement="below">
                    <h2 className="text-xl font-bold text-gray-900">
                      وضعیت تماس‌ها
                      <TooltipInfoIcon />
                    </h2>
                  </HintTooltip>
                  <p className="mt-1.5 text-sm text-gray-500">بر اساس disposition در CDR</p>
                </div>
                <ul className="space-y-4 p-5 sm:p-6">
                  {(report.by_disposition || []).map((item) => {
                    const pct =
                      dispositionTotal > 0
                        ? Math.round((item.count / dispositionTotal) * 100)
                        : 0;
                    const barClass =
                      DISPOSITION_COLORS[item.disposition] || 'bg-slate-400';
                    return (
                      <li key={item.disposition}>
                        <div className="mb-1.5 flex items-center justify-between text-sm">
                          <HintTooltip
                            hint={DISPOSITION_HINTS[item.disposition] || `وضعیت CDR: ${item.disposition}`}
                            placement="below"
                          >
                            <span className="font-medium text-gray-700">
                              {item.label}
                              <TooltipInfoIcon />
                            </span>
                          </HintTooltip>
                          <span className="tabular-nums text-gray-900">
                            {item.count.toLocaleString('fa-IR')}
                            <span className="mr-1 text-xs text-gray-400">({pct}٪)</span>
                          </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                          <div
                            className={`h-full rounded-full ${barClass} transition-all`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md shadow-gray-200/30 ring-1 ring-gray-100/80">
              <div className="border-b border-gray-100 bg-gradient-to-l from-slate-50/80 to-white px-5 py-4 sm:px-6">
                <HintTooltip hint={PAGE_HINTS.operatorsSection} placement="below">
                  <h2 className="text-xl font-bold text-gray-900">
                    عملکرد اپراتورها
                    <TooltipInfoIcon />
                  </h2>
                </HintTooltip>
                <p className="mt-1 text-sm text-gray-500">
                  روی هر ردیف کلیک کنید تا جزئیات کامل تماس‌ها و زمان فعال/استراحت را ببینید
                </p>
              </div>

              {topOperators.length === 0 ? (
                <div className="px-6 py-12 text-center text-sm text-gray-500">
                  داده‌ای برای این روز یافت نشد.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50 text-gray-600">
                        <TableHeaderWithTooltip label="#" hint={OPERATOR_COLUMN_HINTS.rank} />
                        <TableHeaderWithTooltip label="کاربر" hint={OPERATOR_COLUMN_HINTS.operator} />
                        <TableHeaderWithTooltip label="ورودی" hint={OPERATOR_COLUMN_HINTS.inbound} />
                        <TableHeaderWithTooltip label="خروجی" hint={OPERATOR_COLUMN_HINTS.outbound} />
                        <TableHeaderWithTooltip label="پاسخ" hint={OPERATOR_COLUMN_HINTS.inboundAnswered} />
                        <TableHeaderWithTooltip label="بدون پاسخ" hint={OPERATOR_COLUMN_HINTS.missed} />
                        <TableHeaderWithTooltip label="فعال" hint={OPERATOR_COLUMN_HINTS.activeSeconds} />
                        <TableHeaderWithTooltip label="استراحت" hint={OPERATOR_COLUMN_HINTS.restSeconds} />
                        <TableHeaderWithTooltip label="مدت مکالمه" hint={OPERATOR_COLUMN_HINTS.talkSeconds} />
                        <TableHeaderWithTooltip
                          label="نرخ پاسخ"
                          hint={OPERATOR_COLUMN_HINTS.answerRate}
                          className="min-w-[120px]"
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {topOperators.map((row) => (
                        <tr
                          key={row.admin_id}
                          onClick={() => openOperatorDetail(row.admin_id)}
                          className="cursor-pointer border-b border-gray-50 transition hover:bg-indigo-50/60"
                        >
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold ${
                                row.rank === 1
                                  ? 'bg-amber-100 text-amber-700'
                                  : row.rank === 2
                                    ? 'bg-slate-200 text-slate-700'
                                    : row.rank === 3
                                      ? 'bg-orange-100 text-orange-700'
                                      : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              {row.rank.toLocaleString('fa-IR')}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-semibold text-gray-900">{row.username}</p>
                            <p className="mt-0.5 text-xs text-gray-400">
                              {(row.extensions || []).length > 0
                                ? `داخلی‌ها: ${(row.extensions || [])
                                    .map((e) => toPersianDigits(e))
                                    .join('، ')}`
                                : 'بدون داخلی ثبت‌شده'}
                            </p>
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-700">
                            {row.inbound.toLocaleString('fa-IR')}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-700">
                            {row.outbound.toLocaleString('fa-IR')}
                          </td>
                          <td className="px-4 py-3 tabular-nums font-semibold text-emerald-600">
                            {(row.inbound_answered ?? 0).toLocaleString('fa-IR')}
                          </td>
                          <td className="px-4 py-3 tabular-nums font-semibold text-rose-600">
                            {row.missed.toLocaleString('fa-IR')}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-teal-700">
                            {formatDuration(row.active_seconds ?? 0)}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-amber-700">
                            {formatDuration(row.rest_seconds ?? 0)}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-700">
                            {formatDuration(row.talk_seconds)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex min-w-[100px] items-center gap-2">
                              <span
                                className={`w-9 shrink-0 text-left text-sm font-semibold tabular-nums ${
                                  row.answerPct >= 80
                                    ? 'text-emerald-600'
                                    : row.answerPct >= 50
                                      ? 'text-amber-600'
                                      : 'text-rose-600'
                                }`}
                              >
                                {row.answerPct.toLocaleString('fa-IR')}٪
                              </span>
                              <div
                                className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100"
                                role="presentation"
                                aria-hidden
                              >
                                <div
                                  className={`h-full rounded-full transition-all ${
                                    row.answerPct >= 80
                                      ? 'bg-emerald-500'
                                      : row.answerPct >= 50
                                        ? 'bg-amber-400'
                                        : 'bg-rose-400'
                                  }`}
                                  style={{ width: `${row.answerPct}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : null}

      {selectedOperatorAdminId != null &&
        createPortal(
          <div
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-3 sm:p-6"
            onClick={closeOperatorDetail}
            role="presentation"
          >
            <div
              className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
              style={{ direction: 'rtl' }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-start justify-between gap-3 border-b border-gray-100 bg-gradient-to-l from-indigo-700 to-slate-800 px-5 py-4 text-white">
                <div>
                  <h3 className="text-lg font-bold">
                    {operatorDetail?.username || 'کاربر'}
                  </h3>
                  <p className="mt-1 text-sm text-indigo-100">
                    {(operatorDetail?.extensions || []).length > 0
                      ? `داخلی‌ها: ${(operatorDetail?.extensions || [])
                          .map((e) => toPersianDigits(e))
                          .join('، ')}`
                      : 'بدون داخلی'}
                    {dateRange?.label ? ` · ${dateRange.label}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeOperatorDetail}
                  className="rounded-lg px-3 py-1.5 text-sm hover:bg-white/15"
                >
                  بستن
                </button>
              </div>

              <div className="overflow-y-auto p-5">
                {operatorDetailLoading && (
                  <p className="py-10 text-center text-sm text-gray-500">در حال بارگذاری جزئیات...</p>
                )}
                {operatorDetailError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {operatorDetailError}
                  </div>
                )}
                {operatorDetail && !operatorDetailLoading && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {[
                        { label: 'ورودی', value: operatorDetail.inbound, className: 'text-sky-700' },
                        { label: 'خروجی', value: operatorDetail.outbound, className: 'text-violet-700' },
                        {
                          label: 'پاسخ',
                          value: operatorDetail.inbound_answered,
                          className: 'text-emerald-700',
                        },
                        {
                          label: 'بدون پاسخ',
                          value: operatorDetail.missed,
                          className: 'text-rose-700',
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-3"
                        >
                          <p className="text-xs text-gray-500">{item.label}</p>
                          <p className={`mt-1 text-xl font-bold tabular-nums ${item.className}`}>
                            {item.value.toLocaleString('fa-IR')}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div className="rounded-xl border border-teal-100 bg-teal-50/70 px-4 py-3">
                        <p className="text-xs text-teal-800">زمان فعال بودن در صف</p>
                        <p className="mt-1 text-base font-bold text-teal-900">
                          {formatDuration(operatorDetail.active_seconds)}
                        </p>
                      </div>
                      <div className="rounded-xl border border-amber-100 bg-amber-50/70 px-4 py-3">
                        <p className="text-xs text-amber-800">زمان استراحت</p>
                        <p className="mt-1 text-base font-bold text-amber-900">
                          {formatDuration(operatorDetail.rest_seconds)}
                        </p>
                      </div>
                      <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 px-4 py-3">
                        <p className="text-xs text-indigo-800">مدت مکالمه / نرخ پاسخ</p>
                        <p className="mt-1 text-base font-bold text-indigo-900">
                          {formatDuration(operatorDetail.talk_seconds)}
                          <span className="mr-2 text-sm font-semibold text-emerald-700">
                            · {operatorDetail.answer_rate_pct.toLocaleString('fa-IR')}٪
                          </span>
                        </p>
                      </div>
                    </div>

                    {operatorDetail.presence_segments.length > 0 && (
                      <div>
                        <h4 className="mb-2 text-sm font-bold text-gray-900">بازه‌های فعال / استراحت</h4>
                        <div className="max-h-40 overflow-y-auto rounded-xl border border-gray-100">
                          <table className="min-w-full text-xs">
                            <thead className="bg-gray-50 text-gray-600">
                              <tr>
                                <th className="px-3 py-2 text-right font-medium">نوع</th>
                                <th className="px-3 py-2 text-right font-medium">از</th>
                                <th className="px-3 py-2 text-right font-medium">تا</th>
                                <th className="px-3 py-2 text-right font-medium">مدت</th>
                              </tr>
                            </thead>
                            <tbody>
                              {operatorDetail.presence_segments.map((seg, idx) => (
                                <tr key={`${seg.start}-${idx}`} className="border-t border-gray-50">
                                  <td className="px-3 py-2">
                                    <span
                                      className={
                                        seg.type === 'active'
                                          ? 'font-semibold text-teal-700'
                                          : 'font-semibold text-amber-700'
                                      }
                                    >
                                      {seg.type === 'active' ? 'فعال' : 'استراحت'}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 tabular-nums text-gray-600" dir="ltr">
                                    {toPersianDigits(seg.start)}
                                  </td>
                                  <td className="px-3 py-2 tabular-nums text-gray-600" dir="ltr">
                                    {toPersianDigits(seg.end)}
                                  </td>
                                  <td className="px-3 py-2 text-gray-700">
                                    {formatDuration(seg.seconds)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <div>
                      <h4 className="mb-2 text-sm font-bold text-gray-900">
                        لیست تماس‌ها
                        <span className="mr-2 text-xs font-normal text-gray-400">
                          ({operatorDetail.calls_total.toLocaleString('fa-IR')} مورد)
                        </span>
                      </h4>
                      {operatorDetail.calls.length === 0 ? (
                        <p className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                          تماسی برای این داخلی در بازه انتخاب‌شده نیست.
                        </p>
                      ) : (
                        (() => {
                          const totalCalls = operatorDetail.calls.length;
                          const totalPages = Math.max(
                            1,
                            Math.ceil(totalCalls / OPERATOR_CALLS_PAGE_SIZE)
                          );
                          const safePage = Math.min(operatorCallsPage, totalPages);
                          const startIdx = (safePage - 1) * OPERATOR_CALLS_PAGE_SIZE;
                          const pageCalls = operatorDetail.calls.slice(
                            startIdx,
                            startIdx + OPERATOR_CALLS_PAGE_SIZE
                          );
                          const fromNum = startIdx + 1;
                          const toNum = startIdx + pageCalls.length;

                          return (
                            <div
                              className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                              dir="rtl"
                            >
                              <div className="overflow-x-auto">
                                <table className="w-full border-collapse text-sm">
                                  <thead>
                                    <tr className="bg-slate-100 text-slate-700">
                                      <th className="border-b border-slate-200 px-3 py-3 text-right text-xs font-bold">
                                        زمان
                                      </th>
                                      <th className="border-b border-slate-200 px-3 py-3 text-right text-xs font-bold">
                                        نوع
                                      </th>
                                      <th className="border-b border-slate-200 px-3 py-3 text-right text-xs font-bold">
                                        داخلی
                                      </th>
                                      <th className="border-b border-slate-200 px-3 py-3 text-right text-xs font-bold">
                                        شماره
                                      </th>
                                      <th className="border-b border-slate-200 px-3 py-3 text-right text-xs font-bold">
                                        وضعیت
                                      </th>
                                      <th className="border-b border-slate-200 px-3 py-3 text-right text-xs font-bold">
                                        مدت
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pageCalls.map((call, idx) => (
                                      <tr
                                        key={`${call.unique_id || call.call_time}-${startIdx + idx}`}
                                        className={`border-b border-slate-100 last:border-b-0 ${
                                          idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/70'
                                        } hover:bg-indigo-50/50`}
                                      >
                                        <td className="px-3 py-2.5 text-right text-xs tabular-nums text-slate-600">
                                          <span dir="ltr" className="inline-block">
                                            {toPersianDigits(call.call_time)}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                          <span
                                            className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                                              call.direction === 'inbound'
                                                ? 'bg-sky-50 text-sky-700'
                                                : call.direction === 'outbound'
                                                  ? 'bg-violet-50 text-violet-700'
                                                  : 'bg-slate-100 text-slate-600'
                                            }`}
                                          >
                                            {call.direction_label}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-xs tabular-nums text-slate-600">
                                          <span dir="ltr" className="inline-block">
                                            {toPersianDigits(call.extension || '—')}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-xs font-medium tabular-nums text-slate-800">
                                          <span dir="ltr" className="inline-block">
                                            {toPersianDigits(call.peer_number || '—')}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-right">
                                          <span
                                            className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold ${
                                              call.disposition === 'ANSWERED'
                                                ? 'bg-emerald-50 text-emerald-700'
                                                : call.disposition === 'NO ANSWER'
                                                  ? 'bg-rose-50 text-rose-700'
                                                  : 'bg-slate-100 text-slate-600'
                                            }`}
                                          >
                                            {call.disposition_label}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-xs text-slate-700">
                                          {formatDuration(call.billsec || 0)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              {totalPages > 1 && (
                                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
                                  <p className="text-xs text-slate-500">
                                    {toPersianDigits(String(fromNum))} تا{' '}
                                    {toPersianDigits(String(toNum))} از{' '}
                                    {totalCalls.toLocaleString('fa-IR')}
                                  </p>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      disabled={safePage <= 1}
                                      onClick={() =>
                                        setOperatorCallsPage((p) => Math.max(1, p - 1))
                                      }
                                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                      قبلی
                                    </button>
                                    <span className="min-w-[5rem] text-center text-xs tabular-nums text-slate-600">
                                      صفحه {safePage.toLocaleString('fa-IR')} از{' '}
                                      {totalPages.toLocaleString('fa-IR')}
                                    </span>
                                    <button
                                      type="button"
                                      disabled={safePage >= totalPages}
                                      onClick={() =>
                                        setOperatorCallsPage((p) =>
                                          Math.min(totalPages, p + 1)
                                        )
                                      }
                                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                      بعدی
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default AdminCallCenterReports;
